//   _____                                _           _
//  |  __ \                              | |         | |
//  | |  | | ___ _ __  _ __ ___  ___ __ _| |_ ___  __| |
//  | |  | |/ _ \ '_ \| '__/ _ \/ __/ _` | __/ _ \/ _` |
//  | |__| |  __/ |_) | | |  __/ (_| (_| | ||  __/ (_| |
//  |_____/ \___| .__/|_|  \___|\___\__,_|\__\___|\__,_|
//              | |
//              |_|
//
// We are moving to use the new DragNames Panel (src/views/indexing/components/selectPerson/dragNames/DragNames.js)
// This component should be removed once the rip_sharedImageViewer_dragNamesPanelRefactor flag is removed

import { DndContext, DragOverlay } from '@dnd-kit/core'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import { css } from '@emotion/core'
import zionDebug from '@fs/zion-debug'
import { useFeatureFlags } from '@fs/zion-flags'
import { ContentAdd, NoticeLoading } from '@fs/zion-icon'
import {
  batchCreateRelationship,
  batchMovePersonToOtherRecord,
  generateSls,
  getNestedOrFloatingElementById,
  getNestedSubelementsById,
  getNextArkId,
  getPersonRelationships,
  saveSLSActions,
  useSlsDispatchContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import {
  Autosuggest,
  Button,
  colors,
  DataBlock,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Divider,
  Image,
  InfoSheetPanel,
  InlineButton,
  List,
  Paragraph,
  Row,
  Separator,
  Toggle,
  useOverlay,
} from '@fs/zion-ui'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import ErrorOverlay from '../../../../../shared/components/errorOverlay/ErrorOverlay'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import searchRecordImg from '../../../../images/search-record.png'
import { headers, R2AppName } from '../../../../../shared/services/config'
import {
  autosuggestPrioritySort,
  errorCollector,
  getRecordTypesForAutosuggest,
} from '../../../../../shared/utils/utils'
import useRecords from '../../hooks/useRecords'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import CrossRecordWarningOverlay from '../editPanels/CrossRecordWarningOverlay'
import PanelHeader from '../editPanels/PanelHeader'
import Draggable, { dashedBorderCss, dashedBorderCssStyle } from '../ui/Draggable'
import Droppable from '../ui/Droppable'
import OtherPersonsMentioned from './OtherPersonsMentioned'
import PersonDisplay from './PersonDisplay'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

const addRecordBoxNormalCss = css`
  background-image: ${dashedBorderCss};
  height: 90px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  background-color: ${colors.gray03};
  border: solid 4px ${colors.gray00};
`
const addRecordBoxActiveCss = css`
  background-image: none;
  border: solid 4px ${colors.blue10};
  height: 90px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  background-color: ${colors.gray03};
`
const dropNormalCss = css`
  border: solid 4px ${colors.gray00};
`

const debug = zionDebug('rip:R2:DragNames')

export const generateSaveDispatches = async ({
  recordIdParam,
  personsData,
  currentPartitionKey,
  images,
  recordType,
}) => {
  let recordId = recordIdParam
  let createNewRecordDispatches = []

  // If it's a single person, wrap it into an array
  const persons = Array.isArray(personsData) ? personsData : [personsData]

  // We only need to create the new record once if the recordIdParam is 'new_record'
  if (recordIdParam === 'new_record') {
    recordId = await getNextArkId('record')
    createNewRecordDispatches = [
      {
        type: 'CREATE_ELEMENT',
        value: {
          elementType: 'RECORD',
          partitionKey: currentPartitionKey,
          id: recordId,
          extraArgs: recordType !== '' ? { recordType } : {},
        },
      },
      {
        type: 'ADD_SUB',
        value: {
          superPartitionKey: currentPartitionKey,
          subPartitionKey: currentPartitionKey,
          superId: currentPartitionKey,
          subId: recordId,
          elementType: 'IMAGE',
        },
      },
    ]
  }

  // Use reduce to accumulate all dispatches for all persons
  const dispatches = persons.reduce(
    (acc, person) => {
      const sourceRecord = getNestedSubelementsById(images, person?.recordId)?.[0]
      const destinationRecord = getNestedSubelementsById(images, recordId)?.[0]

      // Add move dispatch for the person
      const personMoveDispatches = batchMovePersonToOtherRecord({
        person,
        destRecordId: recordId,
        sourceRecPartitionKey: sourceRecord?.partitionKey,
        destRecPartitionKey: destinationRecord ? destinationRecord.partitionKey : currentPartitionKey,
      })

      // Add the dispatches for this person to the accumulator
      acc.push(...personMoveDispatches)

      return acc
    },
    [...createNewRecordDispatches]
  )

  return dispatches
}

/**
 * @deprecated This component is being replaced by the new DragNames Panel
 */
const DragNamesOld = () => {
  const r2State = useR2StateContext()
  const dispatch = useSlsDispatchContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { dragNamesOverlay, closeOverlay } = useOverlayContext()
  const [t] = useTranslation()
  const heading = t('drag=names.heading', 'Drag Names')
  const instructions = t(
    'drag-names.instructions',
    'Drag names to the correct record group or create a new record. All previous relationships will be deleted once a change is saved.'
  )
  const allRecordsEl = useRef(null)
  const [dragInfo, setDragInfo] = useState({})
  const [error, setError] = useState(false)
  const [multipleRelationShipsWarning, setMultipleRelationShipsWarning] = useState(false)
  const [draggedPerson, setDraggedPerson] = useState()
  const [newRecordType, setNewRecordType] = useState('')
  const [warningPerson, setWarningPerson] = useState()
  const [selectedPersons, setSelectedPersons] = useState([])
  const [isMultiSelect, setIsMultiSelect] = useState(false)
  const [crossRecordRelationships, setCrossRecordRelationships] = useState([])
  const [destRecord, setDestRecord] = useState()
  const [addRecordBoxStyle, setAddRecordBoxStyle] = useState(addRecordBoxNormalCss)
  const activeBorder = `solid 4px ${colors.blue10}`
  const normalBorder = `solid 4px ${colors.gray00}`
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])

  const zionSlsState = useSlsStateContext()
  const { currentImageArk } = zionSlsState

  const { allRecords, floatingPersons, noPeople } = useRecords({
    images: zionSlsState?.images,
    currentImageArk: zionSlsState?.currentImageArk,
    thumbnails,
  })
  const newRecordOverlay = useOverlay()
  const crossRecordWarningOverlay = useOverlay()
  const recordTypeOptions = useMemo(
    () => getRecordTypesForAutosuggest(zionSlsState.types, zionSlsState?.images, currentImageArk, t),
    [t, zionSlsState.types, currentImageArk, zionSlsState?.images]
  )

  const saveSls = useCallback(
    async (dispatches) => {
      const newRecordDispatch = dispatches.find((r) => r.type === 'CREATE_ELEMENT' && r.value.elementType === 'RECORD')
      const newRecordId = newRecordDispatch?.value?.id || ''
      const sls = generateSls(dispatches)
      if (sls?.length) {
        debug('saveSls: ', { sls, dispatches })
        try {
          await saveSLSActions({
            actions: sls,
            userId: r2State.userId,
            appName: R2AppName,
            fsUserAgentChain: headers['FS-User-Agent-Chain'],
            splunkDebugModeArguments: slsActionsSplunkDebugger.isOn
              ? { theFileFromWhichSaveWasCalled: 'DragNames' }
              : {},
          })
          // We want to put the new record at the top so the user can see it was added.  Before it was going to the bottom.
          // They had to scroll to see it, which is annoying
          dispatch({
            type: 'BATCH_DISPATCHES',
            value: newRecordId ? dispatches.concat({ type: 'NEW_RECORD_AT_TOP', value: newRecordId }) : dispatches,
          })
        } catch (err) {
          setError(err)
          errorCollector(err, {
            codeId: 66,
            sls,
            status: err?.response?.status,
            responseBody: err?.response?.data,
          })
        }
      }
    },
    [dispatch, r2State.userId, slsActionsSplunkDebugger.isOn]
  )

  const savePersonInOtherRecord = useCallback(
    async (recordIdParam, personsData, currentPartitionKey, recordType) => {
      let dispatches = []
      let relationshipDispatches = []

      if (isMultiSelect) {
        const asyncCollection = (
          await Promise.all(
            personsData.map(async (person) => {
              const allRelationPerson = getPersonRelationships(person, zionSlsState.images)

              // Filter relationships that aren’t part of personsData
              const missingRelationships = allRelationPerson.filter(
                (relation) => !personsData.some((p) => p.id === relation.otherPersonId)
              )

              // If there are missing relationships
              if (missingRelationships.length > 0) {
                if (!warningPerson) {
                  setWarningPerson(personsData)
                  setMultipleRelationShipsWarning(true)
                  setDestRecord(recordIdParam)
                  return false
                }
                setWarningPerson(null)
              }
              // Filter relationships for personsData only
              const validRelations = allRelationPerson.filter((relation) =>
                personsData.some((personData) => personData.id === relation.otherPersonId)
              )

              validRelations.forEach((otherPerson) => {
                // verify if is second or first
                const isSecond = otherPerson.relKey.includes('_SECOND')
                const firstPerson = isSecond ? person : otherPerson
                const secondPerson = isSecond ? otherPerson : person
                const primaryRelation = batchCreateRelationship({
                  partitionKey: firstPerson.partitionKey,
                  person1ID: firstPerson.id,
                  person2ID: secondPerson.otherPersonId,
                  person1PartitionKey: firstPerson.partitionKey,
                  person2PartitionKey: secondPerson.partitionKey,
                  relType: secondPerson.fieldType,
                })

                // if the relation is not primary we are not going to get anything.
                if (!primaryRelation) return

                relationshipDispatches = [...relationshipDispatches, ...primaryRelation]
              })

              return true
            })
          )
        ).flat()

        // we need to valid if we display the warning and stop the process
        if (asyncCollection.includes(false)) {
          return
        }

        dispatches = await generateSaveDispatches({
          recordIdParam,
          personsData,
          currentPartitionKey,
          images: zionSlsState.images,
          recordType,
        })
      } else {
        const existingRelationships = getPersonRelationships(personsData, zionSlsState.images)

        if (existingRelationships.length > 0) {
          if (!warningPerson) {
            setWarningPerson(personsData)
            setCrossRecordRelationships(existingRelationships)
            setDestRecord(recordIdParam)
            crossRecordWarningOverlay.handleClick()
            return
          }
          setWarningPerson(null)
        }

        const singleDispatches = await generateSaveDispatches({
          recordIdParam,
          personsData,
          currentPartitionKey,
          images: zionSlsState.images,
          recordType,
        })

        dispatches.push(...singleDispatches)
      }

      const addDispatches = []
      const removeDispatches = []
      dispatches.forEach((disp) => {
        if (disp.type === 'REMOVE_ELEMENT' || disp.type === 'REM_SUB') {
          removeDispatches.push(disp)
        } else {
          addDispatches.push(disp)
        }
      })

      if (saveChangesFlag.isOn) {
        await saveSls(removeDispatches)
        await saveSls(addDispatches)
        await saveSls(relationshipDispatches)
      } else {
        debug('Saving changes to SLS is disabled')
      }
    },
    [crossRecordWarningOverlay, saveChangesFlag.isOn, warningPerson, zionSlsState.images, saveSls, isMultiSelect]
  )

  const indentationCss = {
    marginInlineStart: '25px',
  }

  const stickyHeaderCss = {
    position: 'fixed',
    zIndex: '1',
    background: colors.gray00,
    paddingRight: '16px',
  }
  const userClosedPanel = () => {
    closeOverlay('dragNames')
  }

  // color the border white on the given records
  const setNormalBorder = (elements) => {
    if (elements) {
      elements.forEach((element) => {
        element.style.border = normalBorder
      })
    }
  }
  // color the border of the active record blue
  const setActiveBorder = (activeRecordElement, event) => {
    const isSameRecord = dragInfo?.person?.recordId === event?.over?.id
    if (activeRecordElement && activeRecordElement.style && !isSameRecord)
      activeRecordElement.style.border = activeBorder
  }

  const handleCheckboxChange = (person, isChecked) => {
    setSelectedPersons((prev) => (isChecked ? [...prev, person] : prev.filter((per) => per.id !== person.id)))
  }

  const saveOnRecord = (recordId, personData, infoDragPerson = null) => {
    if (recordId && personData.recordId !== recordId) {
      if (recordId === 'new_record') {
        setDraggedPerson(infoDragPerson || personData)
        newRecordOverlay.handleClick()
      } else {
        savePersonInOtherRecord(recordId, personData, zionSlsState.currentImageArk)
      }
    }
  }

  const getAllPersons = (currentPersonInfo) =>
    selectedPersons.find((person) => person.id === currentPersonInfo.id)
      ? selectedPersons
      : [...selectedPersons, currentPersonInfo]

  return (
    <InfoSheetPanel
      title={t('drag-names.heading', 'Drag Names')}
      headingColor="purple"
      {...{ ...dragNamesOverlay, close: userClosedPanel }}
    >
      <DndContext
        onDragStart={(event) => {
          const dragPerson = getNestedOrFloatingElementById(zionSlsState.images, event.active.id)
          const dgInfo = {
            isDragging: true,
            person: dragPerson,
            record: getNestedSubelementsById(allRecords, dragInfo?.person?.recordId)?.[0],
          }
          setDragInfo(dgInfo)
        }}
        onDragOver={(event) => {
          const isNewRecord = event?.over?.id === 'new_record'
          const activeRecordElement = allRecordsEl.current.querySelector(`[id="record-${event?.over?.id}"]`)
          const allRecordElements = allRecordsEl.current.querySelectorAll(`[id^="record-"]`)
          const allOtherRecordArr = Array.from(allRecordElements).filter((r) => r?.id !== activeRecordElement?.id)
          setActiveBorder(activeRecordElement, event)
          setNormalBorder(allOtherRecordArr)
          setAddRecordBoxStyle(isNewRecord ? addRecordBoxActiveCss : addRecordBoxNormalCss)
        }}
        onDragEnd={(event) => {
          setNormalBorder(allRecordsEl.current.querySelectorAll(`[id^="record-"]`))
          const activeRecord = allRecordsEl.current.querySelectorAll(`[id="record-${event?.over?.id}"]`)
          if (activeRecord && activeRecord.style) activeRecord.style.border = `solid 4px ${colors.gray00}`
          if (isMultiSelect) {
            const allPersonsToMove = getAllPersons(dragInfo.person)

            // Save all persons to the destination record
            saveOnRecord(event.over.id, allPersonsToMove, dragInfo.person)
          } else {
            saveOnRecord(event.over.id, dragInfo.person)
          }

          setDragInfo(null)
          if (event.over.id !== 'new_record') {
            setSelectedPersons([])
          }
        }}
        onDragCancel={() => {
          setDragInfo(null)
        }}
      >
        <div css={stickyHeaderCss}>
          <PanelHeader heading={heading} caption={instructions} headingSize="H5" />
          <Droppable key="new_record" id="new_record">
            <div css={addRecordBoxStyle} data-testid="droppableChild">
              <Button emphasis="low" Icon={ContentAdd}>
                {t('drag-names.button.newRecord', 'New Record Group')}
              </Button>
              <div css={indentationCss}>
                <Paragraph size="xs">
                  {t('drag-names.button.dragToCreateRecord', 'Drag name to create a new record group.')}
                </Paragraph>
              </div>
            </div>
          </Droppable>
          <Separator size="xs" />
          <Divider fullWidth />
        </div>
        <Separator size="xxl" />
        <div />
        <Separator size="xxl" />
        <Toggle
          label={t('drag-names.toggle.multiple', 'Move multiple names')}
          name="isMultiple"
          checked={isMultiSelect}
          onChange={() => {
            setIsMultiSelect(!isMultiSelect)
          }}
        />
        <div ref={allRecordsEl}>
          {allRecords?.map((record, i) => {
            const persons = record?.subElements?.filter(
              (sub) => sub?.elementType === 'PERSON' && sub?.partitionKey === currentImageArk
            )

            return (
              <div key={record?.id}>
                {persons && persons.length > 0 && i !== 0 && (
                  <>
                    <Separator size="xs" />
                    <Divider fullWidth />
                    <Separator size="xs" />
                  </>
                )}
                <Droppable id={record?.id} dragging={dragInfo?.isDragging}>
                  <div key={record?.id}>
                    {persons && persons.length > 0 && (
                      <div css={dropNormalCss} id={`record-${record?.id}`}>
                        <List>
                          {persons.map((person) => {
                            const style =
                              dragInfo?.isDragging && person?.id === dragInfo?.person?.id ? dashedBorderCssStyle : {}
                            const isSelected = selectedPersons.some((selectedPerson) => selectedPerson.id === person.id)
                            return (
                              <div key={person?.id}>
                                <Draggable id={person?.id} style={style} data-current={record?.id}>
                                  <PersonDisplay
                                    person={person}
                                    t={t}
                                    key={person.id}
                                    record={record}
                                    onCheckboxChange={handleCheckboxChange}
                                    isSelected={isSelected}
                                    isMultiSelect={isMultiSelect}
                                  />
                                </Draggable>
                              </div>
                            )
                          })}
                        </List>
                      </div>
                    )}
                    <OtherPersonsMentioned dragInfo={dragInfo} record={record} movePersonMode />
                  </div>
                </Droppable>
              </div>
            )
          })}
        </div>
        {floatingPersons && floatingPersons?.length > 0 && (
          <div>
            <Separator size="xs" />
            <Divider fullWidth />
            <Separator size="xs" />
            <DataBlock
              heading={t('select-person.title.heading', 'Names not in records')}
              data={t('select-person.title.subheading', 'Add relationships to connect a name to the correct record.')}
            />
            <Separator size="xs" />
            {floatingPersons
              .sort((a, b) => (a.primary > b.primary ? -1 : 1))
              .map((person) => {
                const style = dragInfo?.isDragging && person?.id === dragInfo?.person?.id ? dashedBorderCssStyle : {}
                const isSelected = selectedPersons.some((selectedPerson) => selectedPerson.id === person.id)
                return (
                  <div key={person?.id}>
                    <Draggable key={person?.id} id={person?.id} style={style}>
                      <PersonDisplay
                        person={person}
                        zionSlsState={zionSlsState}
                        t={t}
                        key={person.id}
                        onCheckboxChange={handleCheckboxChange}
                        isSelected={isSelected}
                        isMultiSelect
                      />
                    </Draggable>
                  </div>
                )
              })}
          </div>
        )}
        {!zionSlsState.loading && noPeople && (
          <>
            <Image src={searchRecordImg} alt="" />
            <Paragraph size="sm" centered>
              {t('select-person.subheading.not-indexed', 'This image has not been indexed.')}
            </Paragraph>
          </>
        )}

        {noPeople && zionSlsState.loading && (
          <Row alignX="center">
            <NoticeLoading size="md" />
          </Row>
        )}
        {/** Temporary person draggable that is detached from the list.  It is used to drag the person. */}
        {createPortal(
          <DragOverlay
            modifiers={[restrictToWindowEdges]}
            dropAnimation={null}
            style={{
              borderInlineStart: `4px solid${colors.blue10}`,
              boxShadow: '0 0 5px rgb(68 68 68 / 0.6)',
              background: 'white',
            }}
          >
            {dragInfo && (
              <PersonDisplay person={dragInfo?.person} t={t} key={dragInfo?.person?.id} record={dragInfo?.record} />
            )}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
      <DialogOverlay {...newRecordOverlay} title={t('new-record-dialog.title', 'New Record')}>
        {t('new-record-dialog.content', 'What is the record type? If you are unsure of what to enter, leave it blank.')}
        {/* Force the user to select an option.  SLS Doesn't allow a record without a type */}
        <Autosuggest
          label={t('new-record-dialog.record-types', 'Record Types')}
          placeholder={t('new-record-dialog.record-types', 'Record Types')}
          suggestions={recordTypeOptions}
          onInputTextChange={(text) => {
            if (!text) setNewRecordType('')
          }}
          onSuggestionSelected={(suggestion) => {
            setNewRecordType(suggestion?.key || '')
          }}
          helpMessage={
            <>
              {t('new-record-dialog.help-message', 'What are record types?')}
              &nbsp;
              <InlineButton to="https://www.familysearch.org/en/help/helpcenter/article/what-is-a-record-type">
                {t('new-record-dialog.learn-more', 'Learn More')}
              </InlineButton>
            </>
          }
          clearOnClickAway
          sortFunction={autosuggestPrioritySort}
        />
        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button onClick={() => newRecordOverlay.close()}>{t('new-record-dialog.undo.btn', 'Undo')}</Button>
            <Button
              onClick={async () => {
                newRecordOverlay.close()
                if (isMultiSelect) {
                  const allPersonsToMove = getAllPersons(draggedPerson)

                  // Save all persons to the destination record
                  savePersonInOtherRecord('new_record', allPersonsToMove, zionSlsState.currentImageArk, newRecordType)
                } else {
                  savePersonInOtherRecord('new_record', draggedPerson, zionSlsState.currentImageArk, newRecordType)
                }
                setSelectedPersons([])
              }}
              emphasis="high"
            >
              {t('new-record-dialog.save.btn', 'Save')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
      {warningPerson && (
        <CrossRecordWarningOverlay
          overlay={crossRecordWarningOverlay}
          person={warningPerson}
          existingRelationships={crossRecordRelationships}
          onContinue={() => {
            savePersonInOtherRecord(destRecord, warningPerson, zionSlsState.currentImageArk, newRecordType)
          }}
        />
      )}
      <ErrorOverlay
        error={error}
        setError={setError}
        title={t('save-error-overlay.heading', 'Unable to Save Changes')}
        message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
      />
      <ErrorOverlay
        error={multipleRelationShipsWarning}
        setError={setMultipleRelationShipsWarning}
        title={t('edit-relationships.cross-record-warning-overlay.heading', 'Before Adding this Relationship')}
        message={t(
          'edit-relationships.cross-record-warning-overlay.multiple.content',
          'If you dont move all persons on the relationship the relationship would be lost'
        )}
        label={t('edit-relationships.cross-record-warning-overlay.remove.button', 'Remove Relationships')}
        callback={() => {
          setMultipleRelationShipsWarning(false)
          savePersonInOtherRecord(destRecord, warningPerson, zionSlsState.currentImageArk, newRecordType)
        }}
        useDangerButton
      />
    </InfoSheetPanel>
  )
}

export default DragNamesOld
